import React, { useState, useEffect, useCallback, useRef } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { debugLogger } from '@/utils/debugLogger';
import { OptimizedLazyImage } from '@/components/ui/OptimizedLazyImage';
import { Badge } from '@/components/ui/badge';
import { Award } from 'lucide-react';
import { ImageZoom } from '@/components/chat/ImageZoom';

interface ProfilePhotoCarouselProps {
  photos: string[];
  userName: string;
  isVerified?: boolean;
}

export const ProfilePhotoCarousel = ({
  photos = [],
  userName,
  isVerified = false
}: ProfilePhotoCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string>('');
  const carouselRef = useRef<HTMLDivElement>(null);

  // Debug logging
  useEffect(() => {
    debugLogger.debug('[ProfilePhotoCarousel] Component mounted:', {
      userName,
      photosCount: photos.length,
      photos
    });
  }, [userName, photos]);

  // Sync selected index with scroll using IntersectionObserver
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || !photos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            setSelectedIndex(index);
            debugLogger.debug('[ProfilePhotoCarousel] Scrolled to index:', index);
          }
        });
      },
      { root: carousel, threshold: 0.5 }
    );

    carousel.querySelectorAll('[data-index]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [photos]);

  // Scroll to slide on thumbnail click
  const handleThumbClick = useCallback((index: number) => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    
    const item = carousel.querySelector(`[data-index="${index}"]`);
    if (item) {
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, []);

  // Handle photo click to open modal
  const handlePhotoClick = useCallback((photo: string) => {
    setSelectedPhoto(photo);
    setIsModalOpen(true);
    debugLogger.debug('[ProfilePhotoCarousel] Photo clicked for zoom:', photo);
  }, []);

  // Return empty fallback if no photos available
  if (photos.length === 0) {
    return (
      <div className="relative w-full aspect-[4/5] flex items-center justify-center bg-luxury-purple/20 rounded-lg">
        <Avatar className="h-24 w-24 sm:h-32 sm:w-32">
          <AvatarFallback className="text-3xl sm:text-5xl bg-luxury-purple/40 text-white">
            {userName ? userName.charAt(0) : '?'}
          </AvatarFallback>
        </Avatar>
      </div>
    );
  }

  return (
    <div>
      {/* Main Carousel with CSS Scroll Snap */}
      <div className="relative">
        <div
          ref={carouselRef}
          className="flex overflow-x-scroll snap-x snap-mandatory scroll-smooth rounded-t-lg rounded-b-none"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`
            .carousel-container::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {photos.map((photo, index) => (
            <div
              key={index}
              data-index={index}
              className="snap-center flex-shrink-0 w-full relative aspect-[4/5] overflow-hidden cursor-pointer"
              onClick={() => handlePhotoClick(photo)}
            >
              <OptimizedLazyImage
                src={photo}
                alt={`${userName || 'Perfil'} ${index + 1}`}
                className="w-full h-full object-cover rounded-t-lg rounded-b-none"
                context="card"
                lazy={index > 0}
                onLoad={() => debugLogger.debug('[ProfilePhotoCarousel] Image loaded:', { photo, index })}
                onError={() => debugLogger.warn('[ProfilePhotoCarousel] Image failed:', { photo, index })}
              />

              {/* Badge Verificado - primeira foto */}
              {isVerified && index === 0 && (
                <Badge className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-luxury-gold text-luxury-charcoal font-medium text-xs sm:text-sm z-20">
                  <Award className="h-3 w-3 mr-1" /> Verificado
                </Badge>
              )}
            </div>
          ))}
        </div>

        {/* Thumbnails Overlaid - bottom */}
        {photos.length > 1 && (
          <div className="absolute bottom-4 left-4 right-4 z-30">
            <div className="flex gap-2 p-2 bg-black/60 backdrop-blur-md rounded-lg overflow-x-auto scrollbar-none shadow-lg">
              {photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleThumbClick(index);
                  }}
                  className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-md overflow-hidden border-2 transition-all ${
                    index === selectedIndex
                      ? 'border-luxury-pinkBright scale-110 shadow-lg'
                      : 'border-white/30 opacity-70 hover:opacity-100 hover:border-white/50'
                  }`}
                  aria-label={`Ver foto ${index + 1}`}
                >
                  <OptimizedLazyImage
                    src={photo}
                    alt={`Miniatura ${index + 1}`}
                    className="w-full h-full object-cover"
                    context="avatar"
                    lazy={true}
                    onLoad={() => debugLogger.debug('[ProfilePhotoCarousel] Thumbnail loaded:', { photo, index })}
                    onError={() => debugLogger.warn('[ProfilePhotoCarousel] Thumbnail failed:', { photo, index })}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal de Zoom com Thumbnails */}
      <ImageZoom
        src={selectedPhoto}
        alt={`${userName || 'Perfil'} - Foto ${selectedIndex + 1}`}
        photos={photos}
        showThumbnails={true}
        initialIndex={selectedIndex}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
      >
        <span />
      </ImageZoom>
    </div>
  );
};
